import { createContext, PropsWithChildren, useContext } from 'react'

export type DrawerAnalyticsPropertyValue =
  | string
  | number
  | boolean
  | null
  | undefined

export type DrawerAnalyticsProperties = Record<
  string,
  DrawerAnalyticsPropertyValue
>

export interface DrawerAnalyticsConfig {
  eventName: string
  parentEventName?: string
  properties?: DrawerAnalyticsProperties
}

type DrawerAnalyticsPayload = {
  eventName: string
  properties: DrawerAnalyticsProperties
}

type DrawerAnalyticsContextValue = {
  captureDrawerOpened: (payload: DrawerAnalyticsPayload) => void
}

const noop = () => {}

const DrawerAnalyticsContext = createContext<DrawerAnalyticsContextValue>({
  captureDrawerOpened: noop
})

export const DrawerAnalyticsParentContext = createContext<string | undefined>(
  undefined
)

export const DrawerAnalyticsProvider = ({
  children,
  onCapture,
  getBaseProperties
}: PropsWithChildren<{
  onCapture?: (eventName: string, properties: DrawerAnalyticsProperties) => void
  getBaseProperties?: () => DrawerAnalyticsProperties
}>) => (
  <DrawerAnalyticsContext.Provider
    value={{
      captureDrawerOpened: ({ eventName, properties }) =>
        onCapture?.(
          eventName,
          compactDrawerAnalyticsProperties({
            ...(getBaseProperties?.() ?? {}),
            ...properties
          })
        )
    }}>
    {children}
  </DrawerAnalyticsContext.Provider>
)

export const useDrawerAnalytics = () => useContext(DrawerAnalyticsContext)

export const compactDrawerAnalyticsProperties = (
  properties: DrawerAnalyticsProperties
) =>
  Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  )
